/**
 * Hook for guardian/student dashboard paths.
 * When under /club-academy/:orgSlug/*, returns org-scoped paths.
 * When under /academy/:academySlug/*, returns academy-scoped paths.
 * Returns null if not in academy/org context (e.g. org picker).
 */
import { useGuardianAcademyStore } from '../store/guardianAcademy';
import { useAcademySlug } from './useAcademySlug';
import { useOrgSlug } from './useOrgSlug';
import { useOrgPaths } from './useOrgPaths';
import { buildAcademyPath } from '../lib/academyPaths';
import { getAcademyHostMode } from '../lib/academyPaths';

export function useGuardianPaths() {
  const orgSlug = useOrgSlug();
  const orgPaths = useOrgPaths();
  const slug = useAcademySlug();
  const academy = useGuardianAcademyStore((s) => s.academy);

  if (orgSlug && orgPaths.base) {
    const p = orgPaths.p;
    return {
      base: orgPaths.base,
      dashboard: p('/dashboard')!,
      lessons: p('/lessons')!,
      lesson: (id: string) => p(`/lesson/${id}`)!,
      bookLesson: (lessonId: string) => p(`/book-lesson/${lessonId}`)!,
      bookLessonPay: (lessonId: string, bookingId: string) =>
        `${p(`/book-lesson/${lessonId}`)}?bookingId=${encodeURIComponent(bookingId)}`,
      bookPrivate: p('/book-private')!,
      calendar: p('/calendar')!,
      bookings: p('/bookings')!,
      credits: p('/credits')!,
      profile: p('/profile')!,
      myStudents: p('/my-students')!,
    };
  }

  if (!slug) return null;

  const fields = academy ?? { slug };
  const hostMode = getAcademyHostMode();
  const base = buildAcademyPath(fields, '', hostMode) || '/';
  const p = (sub: string) => buildAcademyPath(fields, sub, hostMode);

  return {
    base,
    dashboard: p('/dashboard'),
    lessons: p('/lessons'),
    lesson: (id: string) => p(`/lesson/${id}`),
    bookLesson: (lessonId: string) => p(`/book-lesson/${lessonId}`),
    bookLessonPay: (lessonId: string, bookingId: string) =>
      `${p(`/book-lesson/${lessonId}`)}?bookingId=${encodeURIComponent(bookingId)}`,
    bookPrivate: p('/book-private'),
    calendar: p('/calendar'),
    bookings: p('/bookings'),
    credits: p('/credits'),
    profile: p('/profile'),
    myStudents: p('/my-students'),
  };
}
